import { useEffect, useRef, useState } from 'react';
import { useAuthImage } from '../../hooks/useAuthImage';

interface Props {
  src: string;
  alt?: string;
  className?: string;
  onClick?: () => void;
}

// Reintentos de una miniatura que no carga (oct 2026, mismo arreglo que la PWA
// de AE Desarrollo). En el móvil, a veces la petición de una foto falla una vez
// (red, o una descarga cortada que el service worker convierte en error) y el
// <img> se quedaba en blanco para siempre, aunque la foto existe: al pulsarla,
// la vista grande la volvía a pedir y salía bien. Ahora se reintenta sola, con
// una espera creciente.
const MAX_RETRIES = 3;
const RETRY_BASE_MS = 800;

// Misma URL con un parámetro extra para que el navegador y el service worker
// la pidan de nuevo en vez de reutilizar el fallo. Azure ignora parámetros
// desconocidos en una URL firmada (SAS): la firma sigue siendo válida. Las
// blob: (fotos pedidas con token) no se tocan.
function withRetryParam(url: string, attempt: number): string {
  if (attempt === 0 || !/^https?:/i.test(url)) return url;
  return `${url}${url.includes('?') ? '&' : '?'}_r=${attempt}`;
}

function Placeholder({ className, onClick }: { className?: string; onClick?: () => void }) {
  return (
    <div
      className={className}
      style={{ background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClick}
    >
      <svg viewBox="0 0 24 24" fill="#ccc" width="32" height="32">
        <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
      </svg>
    </div>
  );
}

export function AuthImage({ src, alt = '', className, onClick }: Props) {
  const objectUrl = useAuthImage(src);
  const [attempt, setAttempt] = useState(0);
  const [failed, setFailed] = useState(false);
  // Foto distinta: se empieza de cero (estado que sigue a la prop, sin efecto).
  const [forUrl, setForUrl] = useState(objectUrl);
  if (forUrl !== objectUrl) {
    setForUrl(objectUrl);
    setAttempt(0);
    setFailed(false);
  }
  const timerRef = useRef<number | null>(null);
  useEffect(() => () => {
    if (timerRef.current != null) window.clearTimeout(timerRef.current);
  }, []);

  if (!objectUrl || failed) return <Placeholder className={className} onClick={onClick} />;

  const handleError = () => {
    if (attempt >= MAX_RETRIES) {
      setFailed(true);
      return;
    }
    if (timerRef.current != null) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      setAttempt((a) => a + 1);
    }, RETRY_BASE_MS * (attempt + 1));
  };

  return (
    <img
      key={attempt}
      src={withRetryParam(objectUrl, attempt)}
      alt={alt}
      className={className}
      onClick={onClick}
      onError={handleError}
      // Fondo gris mientras carga o reintenta: nunca una caja en blanco.
      style={{ background: '#f0f0f0' }}
    />
  );
}
